import type { StateCreator, StoreMutatorIdentifier } from "zustand";

type SyncAcrossTabs = <
  T,
  Mps extends [StoreMutatorIdentifier, unknown][] = [],
  Mcs extends [StoreMutatorIdentifier, unknown][] = [],
>(
  initializer: StateCreator<T, Mps, Mcs>,
) => StateCreator<T, Mps, Mcs>;

type SyncAcrossTabsImpl = <T>(initializer: StateCreator<T, [], []>) => StateCreator<T, [], []>;

interface PersistApi<T> {
  persist: {
    getOptions: () => {
      name?: string;
      partialize: (state: T) => unknown;
      merge: (persistedState: unknown, currentState: T) => T;
    };
    setOptions: (options: { merge: (persistedState: unknown, currentState: T) => T }) => void;
    rehydrate: () => Promise<void> | void;
  };
}

const syncAcrossTabsImpl: SyncAcrossTabsImpl = (initializer) => (set, get, api) => {
  const state = initializer(set, get, api);
  const { persist } = api as unknown as PersistApi<typeof state>;
  const { name, partialize, merge } = persist.getOptions();

  // A value another tab removed is missing in the storage, and a merge keeps what is not there - so the persisted keys
  // return to their initial values before the store's own merge, also when the whole storage was cleared.
  persist.setOptions({
    merge: (persistedState, currentState) =>
      merge(persistedState, { ...currentState, ...(partialize(api.getInitialState()) as Partial<typeof state>) }),
  });

  window.addEventListener("storage", (event) => {
    // A null key means the whole storage was cleared.
    if (event.key === null || event.key === name) {
      persist.rehydrate();
    }
  });

  return state;
};

/**
 * Wraps `persist`, which reads the storage only when the store is created - this keeps a store that other tabs write
 * too on their latest value.
 */
export const syncAcrossTabs = syncAcrossTabsImpl as unknown as SyncAcrossTabs;
